import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/** A token the template may use, shown to the author as `{{token}}`. */
export interface EmailTemplatePlaceholder {
  token: string
  label: string
}

/**
 * The tenant's out-of-specification alert email (z8nrz7eq4h). `isDefault` is
 * true while the tenant has not saved its own.
 */
export interface EmailTemplate {
  subject: string
  body: string
  isDefault: boolean
  updatedAt: string | null
  updatedBy: string | null
  placeholders: EmailTemplatePlaceholder[]
}

/** Longest subject and body the server accepts. */
export const EMAIL_TEMPLATE_SUBJECT_MAX = 200
export const EMAIL_TEMPLATE_BODY_MAX = 5000

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

// Tenant-scoped; writes need users.manage in the selected tenant. A 422 names
// an unknown or malformed placeholder in its message.
export const emailTemplatesApi = {
  getOosAlert: () => request<EmailTemplate>('/email-templates/oos-alert'),

  saveOosAlert: (body: { subject: string; body: string }) =>
    request<EmailTemplate>('/email-templates/oos-alert', { method: 'PUT', body: JSON.stringify(body) }),

  /** Goes back to the default; answers with the default. */
  resetOosAlert: () => request<EmailTemplate>('/email-templates/oos-alert', { method: 'DELETE' }),
}
